import './styles/base.css';
import '@fontsource-variable/fraunces/full.css';
import '@fontsource-variable/recursive/full.css';

import { CONFIG, DEV, readParams, resolveTuning } from './config';
import { createBus } from './bus';
import { detectQuality } from './quality';
import { createChimeWorld } from './physics/chimes';
import { createRenderer } from './render/index';
import { createAtmos } from './render/atmos';
import { createAudio } from './audio/engine';
import { createMuteButton } from './ui/mute';
import { createLogo } from './logo/logo';
import { clamp01, lerp } from './util/math';
import type { PhysicsEvent, PointerInfo, Vec2, Vec3 } from './types';
import { v2, v3 } from './types';

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
const params = readParams();
const quality = detectQuality(params);
const tuning = resolveTuning(params.tuning);
const bus = createBus();

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const atmosCanvas = $<HTMLCanvasElement>('atmos');
const chimeCanvas = $<HTMLCanvasElement>('chimes');
const logoEl = $<HTMLElement>('logo');
const uiEl = $<HTMLElement>('ui');

document.documentElement.dataset.tier = quality.tier;
if (quality.touch) document.documentElement.dataset.touch = '1';
if (quality.reducedMotion) document.documentElement.dataset.reducedMotion = '1';

const world = createChimeWorld({
  tuningHz: tuning.hz,
  ring: CONFIG.ring,
  seed: params.seed ?? CONFIG.seed,
  quality,
});
const renderer = createRenderer({ canvas: chimeCanvas, world, quality, bus });
const atmos = createAtmos({ canvas: atmosCanvas, quality, bus });
const audio = createAudio({
  bus,
  quality,
  tuningHz: tuning.hz,
  tuningName: tuning.name,
  defaultEnabled: CONFIG.audio.defaultEnabled && !params.mute,
});
const logo = createLogo({ container: logoEl, bus, quality });
createMuteButton({ mount: uiEl, audio, bus });

// ---------------------------------------------------------------------------
// Pointers as hands
// ---------------------------------------------------------------------------
const hands = world.hands;
const slotOf = new Map<number, number>();
const TMP3 = v3();

function acquire(e: PointerEvent): number {
  let s = slotOf.get(e.pointerId);
  if (s !== undefined) return s;
  s = hands.findIndex((h) => !h.active);
  if (s < 0) return -1;
  slotOf.set(e.pointerId, s);
  const h = hands[s];
  h.id = e.pointerId;
  h.active = true;
  h.still = 0;
  h.screen.x = e.clientX;
  h.screen.y = e.clientY;
  renderer.screenToWorld(e.clientX, e.clientY, h.pos);
  h.vel.x = h.vel.y = h.vel.z = 0;
  return s;
}

function release(pointerId: number) {
  const s = slotOf.get(pointerId);
  if (s === undefined) return;
  slotOf.delete(pointerId);
  const h = hands[s];
  h.active = false;
  h.pressed = false;
  h.still = 0;
  h.vel.x = h.vel.y = h.vel.z = 0;
}

window.addEventListener('pointermove', (e) => {
  const s = acquire(e);
  if (s < 0) return;
  const h = hands[s];
  h.screen.x = e.clientX;
  h.screen.y = e.clientY;
}, { passive: true });

window.addEventListener('pointerdown', (e) => {
  const s = acquire(e);
  if (s >= 0) hands[s].pressed = true;
  void audio.unlock();
});

window.addEventListener('pointerup', (e) => {
  const s = slotOf.get(e.pointerId);
  if (s === undefined) return;
  hands[s].pressed = false;
  if (e.pointerType !== 'mouse') release(e.pointerId);
});
window.addEventListener('pointercancel', (e) => release(e.pointerId));
document.addEventListener('pointerout', (e) => {
  // relatedTarget is null when the pointer leaves the window entirely
  if (e.relatedTarget === null) release(e.pointerId);
});
window.addEventListener('blur', () => { for (const h of hands) if (h.active) release(h.id); });

window.addEventListener('keydown', (e) => {
  if (e.key === 'm' || e.key === 'M') { audio.toggle(); if (audio.state.enabled) void audio.unlock(); }
  else void audio.unlock();
});

const pointerInfo: PointerInfo = { x: 0, y: 0, vx: 0, vy: 0, active: false, pressed: false, touch: quality.touch };
let primaryScreenPrev: Vec2 | null = null;

function updateHands(dt: number) {
  const k = 1 - Math.exp(-dt * 28);
  let primary = -1;
  for (let i = 0; i < hands.length; i++) {
    const h = hands[i];
    if (!h.active) continue;
    if (primary < 0) primary = i;
    renderer.screenToWorld(h.screen.x, h.screen.y, TMP3);
    const vx = (TMP3.x - h.pos.x) / dt;
    const vy = (TMP3.y - h.pos.y) / dt;
    h.vel.x = lerp(h.vel.x, vx, k);
    h.vel.y = lerp(h.vel.y, vy, k);
    h.vel.z = 0;
    h.pos.x = TMP3.x; h.pos.y = TMP3.y; h.pos.z = CONFIG.hand.planeZ;
    const speed = Math.hypot(h.vel.x, h.vel.y);
    h.still = speed < 0.04 ? h.still + dt : 0;
  }
  if (primary >= 0) {
    const h = hands[primary];
    if (primaryScreenPrev) {
      pointerInfo.vx = lerp(pointerInfo.vx, (h.screen.x - primaryScreenPrev.x) / dt, k);
      pointerInfo.vy = lerp(pointerInfo.vy, (h.screen.y - primaryScreenPrev.y) / dt, k);
    } else {
      primaryScreenPrev = v2();
      pointerInfo.vx = pointerInfo.vy = 0;
    }
    primaryScreenPrev.x = h.screen.x; primaryScreenPrev.y = h.screen.y;
    pointerInfo.x = h.screen.x; pointerInfo.y = h.screen.y;
    pointerInfo.active = true;
    pointerInfo.pressed = h.pressed;
    const nx = (h.screen.x / innerWidth) * 2 - 1;
    const ny = (h.screen.y / innerHeight) * 2 - 1;
    renderer.setParallax(nx, ny);
    atmos.setParallax(nx, ny);
  } else {
    pointerInfo.active = false;
    pointerInfo.pressed = false;
    pointerInfo.vx = pointerInfo.vy = 0;
    primaryScreenPrev = null;
  }
}

// ---------------------------------------------------------------------------
// Strikes, waves, chords
// ---------------------------------------------------------------------------
const lastStrikeAt = new Float64Array(world.tubes.length).fill(-1e9);
let lastChordAt = -1e9;
const screenTmp = v2();

function dispatch(e: PhysicsEvent, now: number) {
  if (e.type === 'damp') { bus.emit('damp', e); return; }
  renderer.worldToScreen(e.pos, screenTmp);
  // Wind strikes are physically soft (velocity mostly 0.1-0.3); a compressive curve keeps
  // their waves visible while a hand's full sweep still reads as the loudest thing.
  const strength = Math.pow(clamp01(e.velocity), 0.6);
  renderer.addWave(screenTmp.x, screenTmp.y, strength);
  bus.emit('strike', { ...e, screen: { x: screenTmp.x, y: screenTmp.y } });

  lastStrikeAt[e.tube] = now;
  if (now - lastChordAt < CONFIG.chord.cooldownSec) return;
  const tubes: number[] = [];
  for (let i = 0; i < lastStrikeAt.length; i++) {
    if (now - lastStrikeAt[i] <= CONFIG.chord.windowSec) tubes.push(i);
  }
  if (tubes.length >= CONFIG.chord.minTubes) {
    lastChordAt = now;
    bus.emit('chord', { tubes, strength: clamp01(tubes.length / world.tubes.length) });
  }
}

// ---------------------------------------------------------------------------
// Resize, visibility, loop
// ---------------------------------------------------------------------------
function onResize() {
  renderer.resize();
  atmos.resize();
  logo.relayout();
  bus.emit('resize', { width: innerWidth, height: innerHeight, dpr: devicePixelRatio || 1 });
}
window.addEventListener('resize', onResize);
window.addEventListener('orientationchange', onResize);
onResize();

let last = performance.now();
let acc = 0;
let raf = 0;
const STEP = CONFIG.physics.stepSeconds;

function frame(nowMs: number) {
  raf = requestAnimationFrame(frame);
  tick(nowMs);
}

/** One frame of the whole piece at wall-clock `nowMs`; shared by rAF and the DEV driver. */
function tick(nowMs: number) {
  const now = nowMs / 1000;
  const dt = Math.min(0.05, Math.max(1e-4, (nowMs - last) / 1000));
  last = nowMs;

  updateHands(dt);

  acc += dt;
  let steps = 0;
  while (acc >= STEP && steps < CONFIG.physics.maxSubsteps) {
    const events = world.step(STEP);
    for (let i = 0; i < events.length; i++) dispatch(events[i], world.time);
    acc -= STEP;
    steps++;
  }
  if (steps === CONFIG.physics.maxSubsteps) acc = 0; // tab was throttled; drop the backlog

  audio.update(dt, world);
  renderer.update(world, dt);
  logo.update(dt, now, pointerInfo.active ? pointerInfo : null);
  atmos.render(now, dt, world.gust);
  renderer.render(now);
}

function start() {
  if (raf) return;
  last = performance.now() - 16;
  acc = 0;
  // Paint the first frame now rather than on the first rAF, so there is never a blank flash
  // (and so a hidden document still shows the piece at rest).
  tick(performance.now());
  raf = requestAnimationFrame(frame);
}
function stop() {
  if (!raf) return;
  cancelAnimationFrame(raf);
  raf = 0;
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) { stop(); audio.suspend(); }
  else { audio.resume(); start(); }
});

start();

if (DEV) {
  /** Drive the piece by hand (headless panes may never fire rAF): advance(seconds, fps). */
  const advance = (seconds: number, fps = 60) => {
    stop();
    const stepMs = 1000 / fps;
    let t = last;
    const frames = Math.round(seconds * fps);
    for (let i = 0; i < frames; i++) { t += stepMs; tick(t); }
    return { time: world.time, energy: world.tubeStates.map((s) => +s.energy.toFixed(2)) };
  };
  let strikeLog: Array<{ t: number; tube: number; kind: string; v: number }> = [];
  bus.on('strike', (e) => { strikeLog.push({ t: +world.time.toFixed(2), tube: e.tube, kind: e.kind, v: +e.velocity.toFixed(2) }); if (strikeLog.length > 400) strikeLog = strikeLog.slice(-400); });
  (window as unknown as { discordant: unknown }).discordant = { world, renderer, atmos, audio, logo, bus, quality, tuning, params, advance, start, stop, strikes: () => strikeLog };
  console.info('%cdiscordant.org%c dev — window.discordant exposes the world', 'font-weight:700', '');
}

export {};
